import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import { GuidePageHeader } from '@/features/guide/components/GuidePageHeader';
import { GuideIdentityStatus } from '@/types';
import { guideIdentityApi, guideIdentityKeys } from '../api/guide-identity.api';
import { GuideIdentityCard } from '../components/GuideIdentityCard';
import { GuideCardActions } from '../components/GuideCardActions';
import { profileIdFromUrl, toGuideCardData } from '../utils/guideCardData';
import { dashboardSectionPanelSx } from '@/shared/styles/surfaces';

export function GuideIdentityDashboardPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: guideIdentityKeys.own(),
    queryFn: () => guideIdentityApi.getOwn(),
  });

  const card = data ? toGuideCardData(data.identity, data.guide, profileIdFromUrl(data.profileUrl)) : null;
  const status = data?.identity.status;

  return (
    <Box>
      <GuidePageHeader
        title="Your Trplin Guide Identity"
        subtitle="Your verified guide card — share it with travelers, or download and print it at ID-card size"
      />

      {isLoading ? (
        <Skeleton variant="rounded" sx={{ width: '100%', maxWidth: 760, aspectRatio: '85.6 / 54', height: 'auto' }} />
      ) : null}

      {isError ? (
        <Alert severity="info">
          {(error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Your Trplin Guide Identity will appear here after your profile is approved.'}
        </Alert>
      ) : null}

      {data && !data.published ? (
        <Paper
          elevation={0}
          role="status"
          sx={{ ...dashboardSectionPanelSx, p: { xs: 3, md: 4 }, maxWidth: 640, textAlign: 'center' }}
        >
          <HourglassTopOutlinedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} aria-hidden />
          <Typography variant="h6" component="h2" fontWeight={800} sx={{ mb: 1 }}>
            Your identity card is being prepared
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            The Trplin team is reviewing your photo and details for your Guide Identity card. You'll get a
            notification as soon as it's published — then you can download, print and share it here.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Guide ID: <strong>{data.identity.guideId}</strong>
          </Typography>
        </Paper>
      ) : null}

      {data && card ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.7fr) minmax(300px, 1fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <GuideIdentityCard card={card} />

          <Paper elevation={0} sx={{ ...dashboardSectionPanelSx, p: 3 }}>
            <Typography variant="subtitle1" component="h2" fontWeight={800} sx={{ mb: 2 }}>
              Credential details
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Guide ID
                </Typography>
                <Typography fontWeight={800} sx={{ letterSpacing: '0.03em' }}>
                  {data.identity.guideId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Identity URL
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {card.identityUrl}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    icon={status === GuideIdentityStatus.ACTIVE ? <CheckCircleIcon /> : undefined}
                    label={status}
                    color={
                      status === GuideIdentityStatus.ACTIVE
                        ? 'success'
                        : status === GuideIdentityStatus.REVOKED
                          ? 'error'
                          : 'warning'
                    }
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              </Box>
            </Stack>

            {card.verification === 'verified' ? (
              <GuideCardActions
                card={card}
                layout="stacked"
                profileLink={{ to: `/g/${data.identity.code}`, label: 'View public identity' }}
              />
            ) : (
              <Alert severity={status === GuideIdentityStatus.REVOKED ? 'error' : 'warning'}>
                Downloads and sharing are available while your identity is active.
              </Alert>
            )}
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
}
